// Head-to-head detector benchmark. Upload a video, pick one or more
// detectors, hit Run: video is decoded once (`<video>.currentTime = t` +
// `seeked`-await), every selected detector processes every frame, and
// per-detector throughput + hit list land in the results table.
//
// One-time cost per session — the Tesseract worker and opencv.js runtime
// are cached across runs and across route mounts, so back-to-back runs
// (e.g. reduce sample rate, re-run) skip the ~4 MB WASM downloads.

import { useRef, useState } from 'react';
import { TextPatternInput } from '../continuous-scan/components/TextPatternInput';
import { DEFAULT_TEXT_PATTERN, parseTextPattern, type ParsedTextPattern } from '../continuous-scan/lib/textPattern';
import type { ActiveFormat } from '../continuous-scan/types';
import { BenchmarkResultsTable } from './BenchmarkResultsTable';
import { runBenchmark, type BenchmarkResult } from './runBenchmark';

const DETECTORS: readonly { key: ActiveFormat; label: string }[] = [
  { key: 'qr', label: 'QR' },
  { key: 'aruco_4x4_1000', label: 'ArUco 4×4 (1000)' },
  { key: 'text', label: 'Text (OCR)' },
];

// 10 Hz gives good signal for detector throughput without paying the
// full ~10 MB/frame ImageData allocation and per-seek harness cost at
// 30 Hz. Operator can bump this if they need finer temporal resolution.
const DEFAULT_SAMPLE_RATE_HZ = 10;
const DEFAULT_MAX_SECONDS = 60;

export function BenchmarkPage() {
  const [file, setFile] = useState<File | null>(null);
  const [selected, setSelected] = useState<Set<ActiveFormat>>(new Set(['qr', 'aruco_4x4_1000', 'text']));
  const [textPattern, setTextPattern] = useState<string>(DEFAULT_TEXT_PATTERN);
  const [sampleRate, setSampleRate] = useState<number>(DEFAULT_SAMPLE_RATE_HZ);
  const [maxSeconds, setMaxSeconds] = useState<number>(DEFAULT_MAX_SECONDS);
  const [running, setRunning] = useState<boolean>(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [results, setResults] = useState<BenchmarkResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const toggleDetector = (key: ActiveFormat) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setResults(null);
    setError(null);
    setProgress(null);
  };

  const start = async () => {
    if (!file || selected.size === 0) return;
    let parsed: ParsedTextPattern | undefined;
    if (selected.has('text')) {
      try {
        parsed = parseTextPattern(textPattern);
      } catch {
        setError('Invalid text pattern — fix the regex before running.');
        return;
      }
    }
    setRunning(true);
    setError(null);
    setResults(null);
    setProgress({ done: 0, total: 0 });
    const abort = new AbortController();
    abortRef.current = abort;
    try {
      const detectors = DETECTORS.map((d) => d.key).filter((k) => selected.has(k));
      const out = await runBenchmark({
        file,
        detectors,
        textPattern: parsed,
        sampleRateHz: sampleRate,
        maxSeconds,
        signal: abort.signal,
        onProgress: (done, total) => setProgress({ done, total }),
      });
      setResults(out);
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') {
        setError('Benchmark cancelled.');
      } else {
        setError((err as Error)?.message ?? 'Benchmark failed.');
      }
    } finally {
      setRunning(false);
      abortRef.current = null;
    }
  };

  const cancel = () => {
    abortRef.current?.abort();
  };

  const canRun = file !== null && selected.size > 0 && !running;

  return (
    <div className="space-y-4 p-4">
      <header>
        <h1 className="text-lg font-semibold text-gray-100">Detector benchmark</h1>
        <p className="text-sm text-gray-400">
          Upload a video, pick detectors, run head-to-head. Every detector processes every
          decoded frame; per-detector timings + first-seen hits appear below.
        </p>
      </header>

      <section className="space-y-3 rounded-lg border border-gray-700 bg-gray-900 p-4">
        <label className="flex flex-col gap-1 text-sm text-gray-300">
          <span>Video file</span>
          <input
            type="file"
            accept="video/*"
            onChange={onFileChange}
            disabled={running}
            className="text-sm"
            data-testid="benchmark-file-input"
          />
          {file && (
            <span className="text-xs text-gray-500">
              {file.name} ({(file.size / 1024 / 1024).toFixed(1)} MB)
            </span>
          )}
        </label>

        <fieldset className="flex flex-wrap items-center gap-3 text-sm text-gray-300">
          <legend className="mr-2 text-gray-400">Detectors</legend>
          {DETECTORS.map((d) => (
            <label key={d.key} className="flex items-center gap-1">
              <input
                type="checkbox"
                checked={selected.has(d.key)}
                onChange={() => toggleDetector(d.key)}
                disabled={running}
                data-testid={`benchmark-detector-${d.key}`}
              />
              {d.label}
            </label>
          ))}
        </fieldset>

        {selected.has('text') && (
          <TextPatternInput value={textPattern} onChange={setTextPattern} />
        )}

        <div className="flex flex-wrap gap-3 text-sm text-gray-300">
          <label className="flex items-center gap-2">
            <span>Sample rate (Hz)</span>
            <input
              type="number"
              min={1}
              max={60}
              value={sampleRate}
              onChange={(e) => setSampleRate(Number(e.target.value) || DEFAULT_SAMPLE_RATE_HZ)}
              disabled={running}
              className="w-20 rounded border border-gray-600 bg-gray-800 px-2 py-1"
              data-testid="benchmark-sample-rate"
            />
          </label>
          <label className="flex items-center gap-2">
            <span>Max seconds</span>
            <input
              type="number"
              min={1}
              max={600}
              value={maxSeconds}
              onChange={(e) => setMaxSeconds(Number(e.target.value) || DEFAULT_MAX_SECONDS)}
              disabled={running}
              className="w-20 rounded border border-gray-600 bg-gray-800 px-2 py-1"
              data-testid="benchmark-max-seconds"
            />
          </label>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={start}
            disabled={!canRun}
            className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-50"
            data-testid="benchmark-run"
          >
            Run
          </button>
          {running && (
            <button
              type="button"
              onClick={cancel}
              className="rounded-md bg-gray-700 px-3 py-1.5 text-sm text-white hover:bg-gray-600"
              data-testid="benchmark-cancel"
            >
              Cancel
            </button>
          )}
          {progress && (
            <span className="text-xs text-gray-400" data-testid="benchmark-progress">
              {progress.done} / {progress.total || '?'} frames
              {progress.total > 0 && (
                <> — {Math.round((progress.done / progress.total) * 100)}%</>
              )}
            </span>
          )}
        </div>
      </section>

      {error && (
        <div
          role="alert"
          className="rounded-md bg-red-900/40 px-4 py-3 text-sm text-red-100 ring-1 ring-red-700"
          data-testid="benchmark-error"
        >
          {error}
        </div>
      )}

      {results && <BenchmarkResultsTable results={results} />}
    </div>
  );
}
